import useUserVarStore from "@app/store/useUserVarStore";
import ShareIcon from "@assets/quick-news/share.svg";
import DefaultImage from "@assets/tabla_default.webp";
import ReactionToolbar from "@components/Blocks/ReactionToolbar/ReactionToolbar";
import {
  ADDITION_LARGE,
  ADDITION_MEDIUM,
  ADDITION_SMALL,
  QN_ANALYTICS_EVENT_BASE,
  QN_SOCIAL_ICON_LIST,
  SIZE_THRESHOLD_MEDIUM,
  SIZE_THRESHOLD_SMALL,
} from "@config/QuickNewsConstants";
import Image from "@elements/ResponsiveImage/Image";
import ShareSocialButton from "@elements/ShareSocial/ShareSocialButton";
import GetReadingTime from "@helper/GetReadingTime";
import { capitalizeFirstCharOnly } from "@helper/stringTransform";
import { formatDateAgo } from "@helper/useDateFormat";
import { useTablaCustomEventAnalyticsCall } from "@hooks/useAnalyticsCall/useTablaCustomEventAnalyticsCall";
import { useArticleReactions } from "@hooks/useArticleReactions";
import { ProcessedArticleData } from "@transformer/useOsResponse";
import { MediaTypes } from "@typings/Opensearch.d";
import { useEffect, useRef, useState } from "react";
import { Link } from "react-router";

import QnParagraph from "./QnParagraph";

export default function QnArticleCard({
  item,
  typeIndex,
  isMobile,
}: {
  item: ProcessedArticleData;
  typeIndex: number;
  isMobile?: boolean;
}) {
  const { userToken } = useUserVarStore();

  const {
    title,
    articleBody,
    linkTo,
    urlPath,
    imageGallery,
    id,
    sectionName,
    createdDate,
  } = item;

  const {
    reactions,
    userReactions,
    handleReactionClick,
    reactionsError,
    showReactions,
  } = useArticleReactions({
    articleId: id,
    userToken,
    publishDate: createdDate,
    uniqueSectionName: sectionName,
  });

  const TablaCustomEvent = useTablaCustomEventAnalyticsCall();
  const readingTime = GetReadingTime(articleBody);

  const renderItems = imageGallery.filter(
    (card) => card.type === MediaTypes.IMAGE,
  );
  const imageItem = renderItems.length > 0 ? renderItems[0] : null;

  const [isDescriptionOpen, setDescriptionIsOpen] = useState(false);
  const [reduceSize, setReduceSize] = useState(100);

  // Ref to image element (safer than querySelector)
  const imgRef = useRef<HTMLImageElement | null>(null);

  const handleOnReadMoreClick = () => {
    TablaCustomEvent({
      ...QN_ANALYTICS_EVENT_BASE,
      clickAction: `read full article::${typeIndex + 1}`,
      clickLabel: title,
    });
  };

  // Toggle article description
  const toggleDescription = () => {
    setDescriptionIsOpen((prev) => !prev);
  };

  function getAddition(reduceSize: number): number {
    if (reduceSize < SIZE_THRESHOLD_SMALL) return ADDITION_SMALL;
    if (reduceSize < SIZE_THRESHOLD_MEDIUM) return ADDITION_MEDIUM;
    return ADDITION_LARGE;
  }

  // Smooth height adjustment for article image
  useEffect(() => {
    const el = imgRef.current;
    if (!el) return;

    if (isDescriptionOpen) {
      const addition = getAddition(reduceSize);
      el.style.height = `${el.offsetHeight - (reduceSize + addition)}px`;
    } else {
      el.style.removeProperty("height");
    }
  }, [isDescriptionOpen, reduceSize, id]);

  return (
    <div
      data-testid="qn-article-card"
      className="relative grid h-full w-full bg-white pb-5 rounded-3xl"
      style={{ gridTemplateRows: "auto 1fr" }}
    >
      {/* Article Image */}
      <div className="relative">
        <Image
          ref={imgRef}
          src={imageItem?.image?.url ?? DefaultImage}
          displayWidth={450}
          srcWidth={450}
          srcHeight={isDescriptionOpen ? 108 : 300}
          alt={imageItem?.altText ?? title}
          className="qn-article-img w-full h-[270px] md:h-[280px] xs:h-[230px] duration-500 pointer-events-none"
        />
      </div>

      {/* Content Block */}
      <div className="px-4 pt-2.5 text-sm text-grey-700 md:text-base">
        <h1 className="qn-article-title line-clamp-3 text-xl md:text-2xl font-primary font-bold leading-[140%] text-grey-100">
          {title}
        </h1>

        <div className="my-2.5 py-2 font-opensans text-grey-500 text-sm font-normal leading-[150%]">
          <span>{capitalizeFirstCharOnly(sectionName || "")}</span>
          <span className="float-right lowercase">
            {formatDateAgo(createdDate)}
          </span>
        </div>

        <div className="text-grey-550">
          <QnParagraph
            articleBody={articleBody}
            toggleDescription={toggleDescription}
            isDescriptionOpen={isDescriptionOpen}
            setReduceSize={setReduceSize}
          />
        </div>
      </div>

      {/* Action Bar */}
      <div className="actions absolute bottom-0 z-30 flex w-full items-center justify-between gap-1 md:gap-3 bg-white px-4 py-4">
        <Link
          reloadDocument
          to={`${urlPath ? urlPath : linkTo}${isMobile ? "?ref=quick-news" : "?ref=quick-news"}`}
          target="_blank"
          rel="noopener noreferrer"
          onClick={handleOnReadMoreClick}
          className="flex h-10 w-[80%] flex-[1_0_0] items-center justify-center gap-2 text-center text-grey-500 text-sm font-bold leading-[150%] border border-grey-800"
        >
          Read full article - {readingTime} min read
        </Link>

        <ReactionToolbar
          articleId={item.id}
          showReactions={showReactions}
          reactions={reactions}
          userReactions={userReactions}
          reactionsError={reactionsError}
          handleReactionClick={handleReactionClick}
          // expandStyle="-top-24 -right-12"
          qnStyle={true}
        />

        <ShareSocialButton
          socialIconList={QN_SOCIAL_ICON_LIST}
          pagePath={`/quick-news/${item.id}`}
          mbPopupClassName="absolute right-0"
          shareIcon={ShareIcon}
          quickNews={true}
          onClickGaEvent={() => {
            TablaCustomEvent({
              ...QN_ANALYTICS_EVENT_BASE,
              clickAction: `share::${typeIndex + 1}`,
              clickLabel: item.title,
            });
          }}
        />
      </div>
    </div>
  );
}
